import React, { useCallback, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { closeOnboardingCompleted, selectOnboardingCompletedOpen } from '../../../../../store/onboarding/onboardingUiSlice'
import { useLoginToAcademyMutation } from '../../../../../store/auth/auth'
import { Button } from '../../../../../components/Button/Button'
import classNames from 'classnames'
import styles from './OnboardingCompletedModal.module.scss'
import { useTracking } from '../../../../../apps/professional-app/fa/common/tracking/hooks'
import { GrowthTrackEvent } from '../../../../../apps/professional-app/fa/common/tracking'

export const OnboardingCompletedModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const show = useAppSelector(selectOnboardingCompletedOpen)
  const [loginToAcademy, loginToAcademyMutation] = useLoginToAcademyMutation()
  const academyLinkRef = useRef<HTMLAnchorElement>(null)
  const loading = loginToAcademyMutation.isLoading
  const { trackingClient } = useTracking()

  useEffect(() => {
    const loginUrl = loginToAcademyMutation.data?.data?.loginUrl
    if (loginUrl && academyLinkRef.current && academyLinkRef.current.href !== loginUrl) {
      academyLinkRef.current.href = loginUrl
      academyLinkRef.current.target = '_blank'
      academyLinkRef.current.click()
      dispatch(closeOnboardingCompleted())
    }
  }, [loginToAcademyMutation.data, dispatch])

  const handleSkip = useCallback(() => {
    trackingClient.track({ eventName: GrowthTrackEvent.ONBOARDING_COMPLETED_SKIP_CLICKED })
    dispatch(closeOnboardingCompleted())
  }, [dispatch, trackingClient])

  const handleViewCourses = useCallback(() => {
    trackingClient.track({ eventName: GrowthTrackEvent.ONBOARDING_COMPLETED_VIEW_COURSES_CLICKED })
    loginToAcademy(null)
  }, [loginToAcademy, trackingClient])

  return (
    <Modal show={show} onHide={handleSkip} centered size='lg' contentClassName='rounded-4 overflow-hidden' animation={false}>
      <a ref={academyLinkRef} href='#' className='d-none' tabIndex={-1} rel='noreferrer' aria-hidden='true' target='_blank' />
      <Modal.Body className='p-0 text-center'>
        <div className='position-relative'>
          <svg className='d-block w-100' viewBox='0 0 800 218' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
            <path
              d='M0 0H800V98.2515L236.667 213.788C236.667 213.788 208.137 221.035 189.333 216.517C171.89 212.326 146.667 194.684 146.667 194.684L0 98.2515V0Z'
              fill='#F9F6FF'
            />
          </svg>
          <svg className='position-absolute top-0 start-0 w-100' viewBox='0 0 800 191' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
            <path
              d='M0 0H800V98.4636L235.333 185.987C235.333 185.987 210.803 194.161 192 189.634C174.557 185.434 149.333 172.311 149.333 172.311L0 98.4636V0Z'
              fill='#EBE5FC'
            />
          </svg>
          <div className='position-absolute top-0 start-0 bottom-0 end-0 d-flex align-items-center justify-content-center'>
            <div className='d-inline-flex align-items-center gap-3'>
              <div className={classNames(styles.logoCircle, 'd-inline-flex align-items-center justify-content-center zocks-bg-color-primary rounded-circle')}>
                <i className={classNames(styles.logoIcon, 'bi bi-mortarboard-fill text-white')} />
              </div>
              <div className='text-start'>
                <div className={classNames(styles.logoLabel, 'zocks-text-primary fw-semibold')}>Zocks</div>
                <div className={classNames(styles.logoName, 'fw-bold')}>Academy</div>
              </div>
            </div>
          </div>
        </div>
        <div className='px-5 zocks-pb-5.0'>
          <h5 className='fw-bold mb-3'>Strengthen Your Experience</h5>
          <p className='text-muted mb-5'>
            Deepen your knowledge while learning time-saving workflows
            <br />
            that can save you 10+ hours a week.
          </p>
          <div className='d-flex gap-3 justify-content-center'>
            <Button variant='primaryGhost' onClick={handleSkip} disabled={loading}>
              Skip for now
            </Button>
            <Button variant='primaryGhost' onClick={handleViewCourses} disabled={loading} loading={loading}>
              View courses
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
